import {UserNotification} from '@/features/notifications/types'

/**
 * The patient a notification's destination belongs to:
 *
 * - their id, when the backend resolved the notification's link to one of their pages
 * - `null` when the notification carries no link at all — which, on a conversation, is exactly
 *   how the backend says that conversation has no patient
 * - `undefined` when it links somewhere belonging to no patient
 *
 * Those three states let a surface weigh its own scope against the task's with one equality:
 * a patient's panel matches that patient's id, the internal one matches `null`, and neither
 * matches `undefined`. The link is read rather than the notification's type on purpose — it is
 * the destination the backend itself chose, and it keeps every surface type-agnostic.
 */
export const getNotificationPatientScope = (notification: UserNotification): string | null | undefined => {
    const {link} = notification.notification
    if (!link) return null
    return /\/patients\/([^/?#]+)/.exec(link)?.[1]
}

/**
 * Whether the notification names a page of its own.
 *
 * A notification without a link has no page destination: opening it puts the operator on the
 * overlaying surface that *is* its destination, on whichever page they happened to be. Read
 * from the link the backend resolved rather than from the notification's type, so a new type
 * landing on an existing surface needs no change here.
 */
export const hasPageDestination = (notification: UserNotification): boolean => Boolean(notification.notification.link)
